import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useUpdateDocumentLibrary } from "@/features/documents/hooks/useDocumentLibrariesQueries";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { UserResponseDtoRole } from "@/types/api";
import type { DocumentLibrary } from "@/types/document";

const ROLE_OPTIONS = [
  UserResponseDtoRole.SUPER_ADMIN,
  UserResponseDtoRole.SUB_ADMIN,
  UserResponseDtoRole.EDITOR,
] as const;

function readAllowedRoles(library: DocumentLibrary): string[] {
  const raw = (library.permissions as Record<string, unknown> | undefined)
    ?.allowedRoles;
  return Array.isArray(raw) ? raw.filter((r): r is string => typeof r === "string") : [];
}

interface LibraryPermissionsDialogProps {
  library: DocumentLibrary;
}

export function LibraryPermissionsDialog({ library }: LibraryPermissionsDialogProps) {
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState<string[]>(() => readAllowedRoles(library));
  const updateMutation = useUpdateDocumentLibrary();

  useEffect(() => {
    if (open) setRoles(readAllowedRoles(library));
  }, [open, library]);

  function toggleRole(role: string, checked: boolean) {
    setRoles((prev) =>
      checked ? [...new Set([...prev, role])] : prev.filter((r) => r !== role),
    );
  }

  async function onSave() {
    try {
      await updateMutation.mutateAsync({
        id: library.id,
        data: { permissions: { allowedRoles: roles } },
      });
      setOpen(false);
    } catch {
      /* mutation surfaces the error */
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
          aria-label="صلاحيات المكتبة"
          title="الصلاحيات"
        >
          <ShieldCheck className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-right">
          <DialogTitle>صلاحيات المكتبة</DialogTitle>
          <DialogDescription>
            الصلاحيات الافتراضية للمستندات الجديدة في «{library.title}».
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {ROLE_OPTIONS.map((role) => (
            <div
              key={role}
              className="flex items-center justify-between rounded-lg border border-border p-3"
            >
              <Label htmlFor={`lib-role-${role}`} className="cursor-pointer">
                {getRoleDisplayName(role)}
              </Label>
              <Checkbox
                id={`lib-role-${role}`}
                checked={roles.includes(role)}
                onCheckedChange={(checked) => toggleRole(role, checked === true)}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={updateMutation.isPending}>
            إلغاء
          </Button>
          <Button onClick={onSave} disabled={updateMutation.isPending}>
            حفظ الصلاحيات
            {updateMutation.isPending && <Loader className="mr-2" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
